import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRightIcon, LockKeyholeIcon, LoaderIcon, ZapIcon } from 'lucide-react';
const WAITLIST_URL = 'https://script.google.com/macros/s/AKfycbwh0J9Vkq6zT5v5-Pe-5GZczrP-7RSoI2ANR30Epc1TUMBa6CtWBK-Hu7W5Kn1EZVlOfw/exec';
export function HeroSection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email) return;
    setStatus('loading');
    try {
      await fetch(WAITLIST_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          email
        })
      });
      setStatus('success');
      setName('');
      setEmail('');
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };
  return <section className="relative pt-36 pb-16 sm:pt-36 sm:pb-20 lg:pt-44 lg:pb-28 overflow-hidden" data-id="element-86">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-87">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-kitty-yellow-100/40 blur-3xl" data-id="element-88" />
        <div className="absolute top-[40%] -left-[10%] w-[50%] h-[50%] rounded-full bg-blue-50/40 blur-3xl" data-id="element-89" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" data-id="element-90">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center" data-id="element-91">
          {/* Left Column */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-xl" data-id="element-92">
            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.1,
            duration: 0.4
          }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-kitty-yellow-100 text-kitty-yellow-800 font-medium text-xs sm:text-sm mb-6 lg:mb-8" data-id="element-93">
              <span className="relative flex h-2 w-2" data-id="element-94">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-kitty-yellow-500 opacity-75" data-id="element-95" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-kitty-yellow-500" data-id="element-96" />
              </span>
              UK Early Access — Limited Spots
            </motion.div>

            {/* Headline */}
            <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-6xl xl:text-7xl font-extrabold text-kitty-dark leading-[1.08] mb-6 lg:mb-4 tracking-tight" data-id="element-97">
              <span className="sm:whitespace-nowrap" data-id="element-98">
                Split money instantly.
              </span>
              <br data-id="element-99" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-kitty-yellow-500 to-orange-400" data-id="element-100">
                No chasing. Ever.
              </span>
            </h1>

            {/* Speed line */}
            <p className="text-sm font-bold text-kitty-yellow-600 uppercase tracking-wider mb-4 lg:mb-3 flex items-center gap-1.5" data-id="element-101">
              <ZapIcon className="w-3.5 h-3.5" data-id="element-102" />
              Set up in under 30 seconds
            </p>

            {/* Subheading — single concise line on mobile */}
            <p className="text-base sm:text-lg lg:text-xl text-gray-500 mb-3 lg:mb-4 leading-relaxed max-w-md" data-id="element-103">
              A shared wallet for friends — chip in, spend together, no one owes
              a thing.
            </p>

            {/* Benefit line — visible on all screens */}
            <p className="text-sm font-semibold text-kitty-dark/35 mb-8 sm:mb-10" data-id="element-104">
              No reminders. No spreadsheets. No maths.
            </p>

            {/* CTA */}
            {status === 'success' ? <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="max-w-md mb-6 bg-green-50 border border-green-200 rounded-2xl px-6 py-4 flex items-center gap-3" data-id="element-105">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-106" />
                <div data-id="element-107">
                  <p className="text-green-800 font-semibold" data-id="element-108">
                    You're on the waitlist 🎉
                  </p>
                  <p className="text-green-700/70 text-sm mt-1" data-id="element-109">
                    We'll let you know as soon as KittyPot is ready. Early users
                    get first access.
                  </p>
                </div>
              </motion.div> : <form onSubmit={handleSubmit} className="max-w-md mb-5 space-y-4" data-id="element-110">
                <input type="text" placeholder="Full name" value={name} onChange={e => setName(e.target.value)} className="w-full pl-5 pr-5 py-[14px] lg:py-4 min-h-[52px] rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-kitty-yellow focus:border-transparent shadow-sm text-gray-900 text-base" required data-id="element-111" />
                <input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} className="w-full pl-5 pr-5 py-[14px] lg:py-4 min-h-[52px] rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-kitty-yellow focus:border-transparent shadow-sm text-gray-900 text-base" required data-id="element-112" />
                <button type="submit" disabled={status === 'loading'} className="w-full lg:w-auto bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-[16px] lg:py-4 min-h-[56px] rounded-full font-bold transition-all active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-113">
                  {status === 'loading' ? <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-114" /> : <>
                      Get Early Access
                      <ArrowRightIcon className="w-4 h-4" data-id="element-115" />
                    </>}
                </button>
                {status === 'error' && <p className="text-red-500 text-sm pl-2" data-id="element-116">
                    Something went wrong. Please try again.
                  </p>}
              </form>}

            {/* Trust microcopy — below button */}
            <div className="flex flex-col gap-1 mb-10 lg:mb-8 pl-1" data-id="element-117">
              <p className="text-[11px] lg:text-sm text-gray-400" data-id="element-118">
                Free to join · No card required · Built for real-life group
                spending
              </p>
              <p className="text-[10px] lg:text-xs text-gray-400 flex items-center gap-1.5" data-id="element-119">
                <LockKeyholeIcon className="w-3 h-3" data-id="element-120" />
                Secure payments powered by Stripe
              </p>
            </div>

            {/* Early access badge */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 mt-2" data-id="element-121">
              <div className="flex items-center gap-3" data-id="element-122">
                <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-gray-100 border border-gray-200" data-id="element-123">
                  <span className="relative flex h-2 w-2" data-id="element-124">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-125" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-126" />
                  </span>
                  <p className="text-sm font-bold text-gray-900 whitespace-nowrap" data-id="element-127">
                    Early access is live
                  </p>
                </div>
              </div>

              <div className="hidden sm:block w-px h-10 bg-gray-200" data-id="element-128" />

              <div className="flex flex-col gap-2.5" data-id="element-129">
                <p className="text-sm text-gray-500" data-id="element-130">
                  Limited spots — be first to try KittyPot.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right Column: App Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          rotate: 2
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          type: 'spring'
        }} className="relative mx-auto w-full max-w-[240px] sm:max-w-[260px] lg:max-w-[320px] mt-8 lg:mt-0" data-id="element-131">
            {/* Floating +£50 badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.5,
            y: 10
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            delay: 1.6,
            duration: 0.4,
            type: 'spring',
            stiffness: 200
          }} className="absolute -top-3 -right-4 z-30 bg-green-500 text-white text-sm font-bold px-3.5 py-1.5 rounded-full shadow-lg" data-id="element-132">
              +£50.00
            </motion.div>

            {/* Floating split notification */}
            <motion.div initial={{
            opacity: 0,
            y: 20,
            scale: 0.9
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            delay: 2.5,
            duration: 0.5,
            type: 'spring'
          }} className="absolute -bottom-4 -left-4 lg:-left-6 z-30 bg-kitty-dark text-white px-4 py-2.5 rounded-2xl shadow-xl max-w-[200px]" data-id="element-133">
              <p className="text-xs font-semibold" data-id="element-134">✓ Balanced automatically</p>
              <p className="text-[10px] text-gray-400" data-id="element-135">
                4 people · everyone's square
              </p>
            </motion.div>

            <div className="relative bg-white rounded-[3rem] shadow-2xl border-[8px] border-gray-900 overflow-hidden aspect-[9/19]" data-id="element-136">
              <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-137">
                <div className="w-32 h-6 bg-gray-900 rounded-b-3xl" data-id="element-138" />
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/gSujjDskykKxeYZXZZDicA/WhatsApp_Image_2026-04-12_at_18.21.25.jpg" alt="KittyPot app screenshot showing a Ski Holiday kitty with £2450.00 balance" className="w-full h-full object-cover object-top" data-id="element-139" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}